import { useState, useCallback } from 'react'
import {
    injectTourStyles,
    cleanupTourElements,
    createTourPopover,
    positionPopover,
    createTourOverlay,
    TourDemoType,
} from '@/utils/tour-styles'

interface TourStep {
    selector: string
    title: string
    description: string
    demo?: TourDemoType
}

const TOUR_STEPS: TourStep[] = [
    // Header actions
    {
        selector: '[data-tour="ticket-tabs"]',
        title: 'Chuyển đổi chế độ xem',
        description:
            'Hai chế độ xem chính:\n• Danh sách: Xem ticket dạng bảng\n• Thống kê: Xem biểu đồ tổng hợp\n• Chọn tab phù hợp với mục đích',
    },
    {
        selector: '[data-tour="ticket-date-range"]',
        title: 'Bộ lọc thời gian',
        description:
            'Lọc ticket theo khoảng thời gian:\n• Hôm nay, 7 ngày, 30 ngày\n• Tuần này, năm nay\n• Hoặc chọn tùy chỉnh\n• Áp dụng cho cả danh sách và thống kê',
    },
    {
        selector: '[data-tour="ticket-export-data"]',
        title: 'Xuất báo cáo (PDF)',
        description:
            'Xuất báo cáo thống kê:\n• In/Lưu dạng PDF\n• Bao gồm tất cả biểu đồ\n• Chỉ hiển thị ở tab Thống kê\n• Dùng để báo cáo định kỳ',
    },
    {
        selector: '[data-tour="ticket-create"]',
        title: 'Tạo ticket mới',
        description:
            'Tạo ticket hỗ trợ mới:\n• Chọn khách hàng, dịch vụ\n• Phân loại: Hỗ trợ/Sự cố/Yêu cầu\n• Gán người xử lý\n• Đặt mức độ ưu tiên',
    },
    // List Tab - Filters
    {
        selector: '[data-tour="ticket-filter-status"]',
        title: 'Lọc theo trạng thái',
        description:
            'Các trạng thái ticket:\n• Mới: Ticket vừa tạo\n• Đang xử lý: Đã có người nhận\n• Chờ phản hồi: Chờ KH\n• Hoàn thành: Đã xong\n• Đóng: Kết thúc',
    },
    {
        selector: '[data-tour="ticket-filter-priority"]',
        title: 'Lọc theo độ ưu tiên',
        description:
            'Mức độ ưu tiên:\n• Critical: Cần xử lý ngay\n• High: Ưu tiên cao\n• Medium: Bình thường\n• Low: Có thể trì hoãn\n• Ảnh hưởng SLA & thời gian phản hồi',
    },
    {
        selector: '[data-tour="ticket-filter-type"]',
        title: 'Lọc theo loại ticket',
        description:
            'Phân loại ticket:\n• Support: Hỗ trợ kỹ thuật\n• Incident: Sự cố dịch vụ\n• Request: Yêu cầu thay đổi\n• Có thể chọn nhiều loại',
    },
    {
        selector: '[data-tour="ticket-filter-assignee"]',
        title: 'Lọc theo người xử lý',
        description:
            'Lọc theo nhân viên kỹ thuật:\n• Xem ticket của từng người\n• Chọn nhiều người cùng lúc\n• Hỗ trợ tìm kiếm theo tên\n• Dùng để phân công/theo dõi',
    },
    {
        selector: '[data-tour="ticket-filter-more"]',
        title: 'Bộ lọc nâng cao',
        description:
            'Các bộ lọc bổ sung:\n• CS Assignees: Nhân viên CS\n• Sale Assignees: Nhân viên Sale\n• Major Incident: Sự cố lớn\n• Ticket Type Key: Loại chi tiết\n• Product Group: Nhóm sản phẩm',
    },
    {
        selector: '[data-tour="ticket-search"]',
        title: 'Tìm kiếm ticket',
        description:
            'Tìm kiếm nhanh:\n• Theo mã ticket (ID)\n• Theo tiêu đề ticket\n• Theo tên khách hàng\n• Theo tên dịch vụ',
    },
    {
        selector: '[data-tour="ticket-export-excel"]',
        title: 'Xuất Excel',
        description:
            'Xuất danh sách ticket:\n• Định dạng Excel (.xlsx)\n• Áp dụng bộ lọc hiện tại\n• Bao gồm tất cả thông tin\n• Dùng để báo cáo chi tiết',
    },
    {
        selector: '[data-tour="ticket-list"]',
        title: 'Danh sách ticket',
        description:
            'Bảng hiển thị ticket:\n• Sắp xếp theo các cột\n• Click vào dòng để xem chi tiết\n• Highlight vàng: Quá hạn phản hồi\n• Checkbox để chọn nhiều',
    },
    // Statistical Tab - Charts
    {
        selector: '[data-tour="ticket-chart-column"]',
        title: 'Biểu đồ ticket theo thời gian',
        description:
            'Số lượng ticket theo giai đoạn:\n• Xem theo ngày/tuần/tháng\n• Phân loại theo trạng thái\n• So sánh xu hướng\n• Nhận diện peak time',
    },
    {
        selector: '[data-tour="ticket-chart-status"]',
        title: 'Phân bố trạng thái',
        description:
            'Biểu đồ tròn trạng thái:\n• Tổng số ticket theo trạng thái\n• Tỷ lệ phần trăm\n• Màu sắc phân biệt\n• Click để xem chi tiết',
    },
    {
        selector: '[data-tour="ticket-chart-response"]',
        title: 'Thời gian xử lý trung bình',
        description:
            'Thời gian phản hồi theo độ ưu tiên:\n• Trung bình thời gian resolve\n• So sánh với SLA target\n• Phân tích theo priority\n• Đánh giá hiệu suất đội ngũ',
    },
    {
        selector: '[data-tour="ticket-chart-top-handler"]',
        title: 'Top người xử lý ticket',
        description:
            'Xếp hạng nhân viên:\n• Số ticket đã xử lý\n• Top 10 người cao nhất\n• Thống kê trong khoảng thời gian\n• Dùng để đánh giá KPI',
    },
    {
        selector: '[data-tour="ticket-chart-overdue"]',
        title: 'Ticket quá hạn theo nhân viên',
        description:
            'Thống kê ticket trễ:\n• Số ticket quá SLA\n• Phân bố theo người xử lý\n• Cảnh báo hiệu suất\n• Cần ưu tiên xử lý',
    },
    {
        selector: '[data-tour="ticket-chart-sla"]',
        title: 'Tỷ lệ tuân thủ SLA',
        description:
            'Đánh giá SLA Compliance:\n• Tỷ lệ đúng hạn (%)\n• So sánh với target\n• Gauge chart trực quan\n• Mục tiêu: ≥ 95%',
    },
    {
        selector: '[data-tour="ticket-chart-satisfaction"]',
        title: 'Hài lòng khách hàng',
        description:
            'Customer Satisfaction Score:\n• Điểm đánh giá từ KH\n• Thang điểm 1-5\n• Dựa trên feedback\n• Mục tiêu: ≥ 4.0',
    },
    {
        selector: '[data-tour="ticket-chart-type"]',
        title: 'Phân bố theo loại ticket',
        description:
            'Thống kê theo loại:\n• Support vs Incident vs Request\n• Tỷ lệ phần trăm\n• Xu hướng loại ticket\n• Phân tích workload',
    },
    {
        selector: '[data-tour="ticket-chart-employee-type"]',
        title: 'Xử lý theo nhân viên & loại',
        description:
            'Bảng chi tiết:\n• Số ticket theo từng NV\n• Phân loại theo type\n• So sánh workload\n• Đánh giá phân công',
    },
]

export const useTicketHomeTour = () => {
    const [isActive, setIsActive] = useState(false)
    const [currentStep, setCurrentStep] = useState(0)

    const getVisibleSteps = useCallback(() => {
        return TOUR_STEPS.filter((step) => {
            const element = document.querySelector(step.selector)
            return element !== null
        })
    }, [])

    const endTour = useCallback(() => {
        cleanupTourElements()
        setIsActive(false)
        setCurrentStep(0)
    }, [])

    const showTourStep = useCallback(
        (stepIndex: number, steps: TourStep[]) => {
            injectTourStyles()
            cleanupTourElements()
            createTourOverlay()

            const step = steps[stepIndex]
            if (!step) return

            const element = document.querySelector(step.selector) as HTMLElement
            if (!element) {
                if (stepIndex < steps.length - 1) {
                    showTourStep(stepIndex + 1, steps)
                } else {
                    endTour()
                }
                return
            }

            element.classList.add('tour-highlight-element')
            element.scrollIntoView({ behavior: 'smooth', block: 'center' })

            setTimeout(() => {
                const isLastStep = stepIndex === steps.length - 1
                const popover = createTourPopover({
                    title: step.title,
                    description: step.description,
                    currentStep: stepIndex + 1,
                    totalSteps: steps.length,
                    onPrev:
                        stepIndex > 0
                            ? () => goToStep(stepIndex - 1, steps)
                            : undefined,
                    onNext: isLastStep
                        ? endTour
                        : () => goToStep(stepIndex + 1, steps),
                    onClose: endTour,
                    isLastStep,
                    demo: step.demo,
                })

                document.body.appendChild(popover)
                positionPopover(popover, element)
            }, 300)
        },
        [endTour]
    )

    const goToStep = useCallback(
        (stepIndex: number, steps: TourStep[]) => {
            setCurrentStep(stepIndex)
            showTourStep(stepIndex, steps)
        },
        [showTourStep]
    )

    const startTour = useCallback(() => {
        const visibleSteps = getVisibleSteps()
        if (visibleSteps.length === 0) {
            console.warn('No visible elements to tour')
            return
        }

        setIsActive(true)
        setCurrentStep(0)
        showTourStep(0, visibleSteps)
    }, [getVisibleSteps, showTourStep])

    return {
        isActive,
        currentStep,
        startTour,
        endTour,
        totalSteps: getVisibleSteps().length,
    }
}

export default useTicketHomeTour
